import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Loader2, Save, Plus, Trash2, RefreshCw, Info } from 'lucide-react';
import { setGroupLabelOverrides, GROUP_LABELS, PSEUDO_GROUPS } from '../../lib/groupLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SURFACES = [
  { key: 'default', label: 'Default', hint: 'Fallback used when a surface has no override' },
  { key: 'stepper', label: 'Stepper', hint: 'Estimator mini-wizard step label' },
  { key: 'short', label: 'Short', hint: 'Chips, dropdowns, tight badges' },
  { key: 'gantt', label: 'Gantt', hint: 'Planner Gantt row labels' },
  { key: 'table', label: 'Table', hint: 'Breakdown & summary tables' },
  { key: 'export', label: 'Export', hint: 'PDF / Word / PPTX exports' },
];

export default function DisplayNamesTab() {
  const [overrides, setOverrides] = useState({});
  const [liveGroups, setLiveGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newGroup, setNewGroup] = useState('');
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [overridesRes, groupsRes] = await Promise.all([
        axios.get(`${API_URL}/api/settings/display-names`, { withCredentials: true }),
        axios.get(`${API_URL}/api/components/groups`, { withCredentials: true }).catch(() => ({ data: { groups: [] } })),
      ]);
      setOverrides(overridesRes.data?.overrides || {});
      setMeta({ updated_at: overridesRes.data?.updated_at, updated_by: overridesRes.data?.updated_by });
      setLiveGroups(groupsRes.data?.groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load display names');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Groups to show: live backend groups + pseudo keys + any existing overrides + static defaults.
  const editableGroups = useMemo(() => {
    const set = new Set([
      ...liveGroups,
      ...Object.keys(PSEUDO_GROUPS),
      ...Object.keys(GROUP_LABELS),
      ...Object.keys(overrides),
    ]);
    return Array.from(set).sort();
  }, [liveGroups, overrides]);

  const updateCell = (group, surface, value) => {
    setOverrides(prev => {
      const next = { ...prev };
      const row = { ...(next[group] || {}) };
      if (value === '') delete row[surface];
      else row[surface] = value;
      if (Object.keys(row).length === 0) delete next[group];
      else next[group] = row;
      return next;
    });
  };

  const clearGroupRow = (group) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[group];
      return next;
    });
  };

  const addGroupRow = () => {
    const key = newGroup.trim();
    if (!key) return;
    if (overrides[key]) { toast.error('That group already has overrides'); return; }
    setOverrides(prev => ({ ...prev, [key]: { default: key } }));
    setNewGroup('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/settings/display-names`, { overrides }, { withCredentials: true });
      setOverrides(res.data?.overrides || {});
      setGroupLabelOverrides(res.data?.overrides || {});
      toast.success(res.data?.message || 'Display names saved');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="display-names-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F2040]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="display-names-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#0F2040]">Display Names</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Per-surface overrides for component group labels. Blank fields fall back to the default group name.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchAll} className="text-[10px] text-slate-500 h-7 px-2"
            data-testid="display-names-refresh-btn">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={save} disabled={saving}
            className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-8" data-testid="display-names-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save Changes
          </Button>
        </div>
      </div>

      {meta.updated_at && (
        <div className="text-[10px] text-slate-400">
          Last updated {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || '—'}
        </div>
      )}

      <Card className="border-slate-200 bg-slate-50/40">
        <CardContent className="py-2.5 px-3 flex items-start gap-2 text-[11px] text-slate-600">
          <Info className="w-3.5 h-3.5 text-[#4ABFCF] mt-0.5 shrink-0" />
          <span>
            Leave a cell blank to use the built-in default. Adding a custom group key here propagates to
            any surface that calls <code className="text-[#0F2040]">getGroupLabel()</code>.
            Overrides load on every user's next page navigation after save.
          </span>
        </CardContent>
      </Card>

      <Card data-testid="display-names-table">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#0F2040]">
            Group overrides <Badge variant="secondary" className="ml-1 text-[9px]">{Object.keys(overrides).length} active</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#0F2040] text-white text-[10px]">
                  <th className="text-left px-2 py-1.5 rounded-tl-md w-64">Group key</th>
                  {SURFACES.map(s => (
                    <th key={s.key} className="text-left px-2 py-1.5" title={s.hint}>{s.label}</th>
                  ))}
                  <th className="text-center px-2 py-1.5 rounded-tr-md w-10"></th>
                </tr>
              </thead>
              <tbody>
                {editableGroups.map((group, i) => {
                  const row = overrides[group] || {};
                  const hasAny = Object.keys(row).length > 0;
                  const isPseudo = !!PSEUDO_GROUPS[group];
                  const isLive = liveGroups.includes(group);
                  // Orphan = override exists for a key that is neither a live backend group nor a pseudo key.
                  const isOrphan = hasAny && !isLive && !isPseudo;
                  return (
                    <tr key={group} className={`${i % 2 ? 'bg-slate-50/50' : ''} ${isOrphan ? 'bg-amber-50/60' : ''}`}
                      data-testid={`display-name-row-${group}`}>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-[#0F2040] break-all">
                        {group}
                        {isPseudo && <Badge variant="secondary" className="ml-1 text-[9px] bg-[#4ABFCF]/10 text-[#4ABFCF] border-[#4ABFCF]/20">pseudo</Badge>}
                        {isOrphan && <Badge variant="secondary" className="ml-1 text-[9px] bg-amber-100 text-amber-700 border-amber-200" title="This override key doesn't match any live group — it has no effect. Consider deleting it.">stale</Badge>}
                      </td>
                      {SURFACES.map(s => (
                        <td key={s.key} className="px-1.5 py-1.5">
                          <Input
                            value={row[s.key] || ''}
                            onChange={e => updateCell(group, s.key, e.target.value)}
                            placeholder="—"
                            className="h-7 text-[11px] px-2"
                            data-testid={`display-name-input-${group}-${s.key}`}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-center">
                        {hasAny && (
                          <Button variant="ghost" size="sm"
                            onClick={() => clearGroupRow(group)}
                            className="text-slate-400 hover:text-red-600 h-6 px-1"
                            title="Clear all overrides for this group"
                            data-testid={`display-name-clear-${group}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-3 px-3 flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[10px] text-slate-500">Add a new group key</Label>
            <Input value={newGroup} onChange={e => setNewGroup(e.target.value)}
              placeholder="e.g. Custom Development"
              onKeyDown={e => { if (e.key === 'Enter') addGroupRow(); }}
              className="h-8 text-xs" data-testid="display-name-add-input" />
          </div>
          <Button size="sm" onClick={addGroupRow} disabled={!newGroup.trim()}
            className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white h-8" data-testid="display-name-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
